import { NetworkStatusService } from '@library/services/qp-network-status/qp-network-status.service';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { EQimaIconName, QimaIconModule } from '@qima/ngx-qima';
import { combineLatest, distinctUntilChanged, map, Observable } from 'rxjs';

@Component({
  selector: 'one-isp-manual-offline-banner',
  standalone: true,
  imports: [TranslateModule, QimaIconModule, AsyncPipe],
  templateUrl: './isp-manual-offline-banner.component.html',
  styleUrls: ['./isp-manual-offline-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IspManualOfflineBannerComponent {
  /** Offline because of the manual toggle or device connectivity. */
  public readonly isOffline$: Observable<boolean>;
  /**
   * Switching back online can only succeed when the inspector chose offline while the device still has
   * network: `NetworkStatusService.toggleManualOffline(false)` throws while `navigator.onLine` is false.
   */
  public readonly canSwitchOnline$: Observable<boolean>;
  public readonly qimaIconNames: typeof EQimaIconName = EQimaIconName;

  private readonly _networkStatusService = inject(NetworkStatusService);

  public constructor() {
    const networkState$ = combineLatest([this._networkStatusService.isOnline$, this._networkStatusService.isBrowserOnline$]);

    this.isOffline$ = networkState$.pipe(
      map(([isOnline, isBrowserOnline]): boolean => !isOnline || !isBrowserOnline),
      distinctUntilChanged()
    );
    this.canSwitchOnline$ = networkState$.pipe(
      map(([, isBrowserOnline]): boolean => this._networkStatusService.isManualOffline && isBrowserOnline),
      distinctUntilChanged()
    );
  }

  public onSwitchOnlineClick(): void {
    try {
      this._networkStatusService.toggleManualOffline(false);
    } catch {
      // Navigator still reports offline.
    }
  }
}
